
import { useState, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';
import { User } from '../types';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null
  });

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedUser = authService.getStoredUser();
        setAuthState({
          user: storedUser,
          isAuthenticated: !!storedUser,
          isLoading: false,
          error: null
        });
      } catch (error) {
        setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: 'Failed to initialize authentication'
        });
      }
    };

    initializeAuth();
  }, []);

  const login = useCallback(async (username: string, password: string, twoFactorCode?: string) => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const result = await authService.login({ username, password, twoFactorCode });
      
      if (result.success && result.user) {
        setAuthState({
          user: result.user,
          isAuthenticated: true,
          isLoading: false,
          error: null
        });
        return { success: true, user: result.user };
      } else {
        setAuthState(prev => ({
          ...prev,
          isLoading: false,
          error: result.error || 'Login failed'
        }));
        return { 
          success: false, 
          error: result.error,
          requiresTwoFactor: result.requiresTwoFactor 
        };
      }
    } catch (error) {
      const errorMessage = 'Authentication service error';
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }));
      return { success: false, error: errorMessage };
    }
  }, []);

  const logout = useCallback(() => {
    authService.logout();
    setAuthState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null
    });
  }, []);

  const hasPermission = useCallback((permission: string): boolean => {
    return authService.hasPermission(authState.user, permission);
  }, [authState.user]);

  const canAccessModule = useCallback((module: string): boolean => {
    return authService.canAccessModule(authState.user, module);
  }, [authState.user]);

  return {
    user: authState.user,
    isAuthenticated: authState.isAuthenticated,
    isLoading: authState.isLoading,
    error: authState.error,
    login,
    logout,
    hasPermission,
    canAccessModule
  };
};
